import { PHASE } from "../../../common/constants.ts";
import setContract from "./setContract.ts";
import { placeYoungInMinors } from "./enforceYouthContracts.ts";
import { g, helpers, logEvent } from "../../util/index.ts";
import type { Phase, Player, PlayerContract } from "../../../common/types.ts";
import genJerseyNumber from "./genJerseyNumber.ts";
import setJerseyNumber from "./setJerseyNumber.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import {
	DRAFT_ELIGIBLE_COUNTRIES,
	ORIGIN_FLAGS,
} from "../../../common/constants.baseball.ts";

const sign = async (
	p: Player,
	tid: number,
	contract: PlayerContract,
	phase: Phase,
) => {
	const isRookie =
		p.stats.length === 0 &&
		p.draft.year === g.get("season") &&
		p.draft.tid === tid;

	// A player signed by the user before he has played anywhere, from a country that has no
	// amateur draft, is an international signing rather than an ordinary free agent pickup. Someone
	// arriving by trade never passes through here, which is why a trade leaves no flag.
	if (
		isSport("baseball") &&
		!p.watch &&
		p.stats.length === 0 &&
		g.get("userTids").includes(tid) &&
		!DRAFT_ELIGIBLE_COUNTRIES.has(helpers.getCountry(p.born.loc))
	) {
		p.watch = ORIGIN_FLAGS.internationalSigning;
	}

	p.tid = tid;
	p.numDaysFreeAgent = 0;
	p.gamesUntilTradable = isRookie ? 0 : Math.round(0.17 * g.get("numGames")); // 14 for basketball, 3 for football
	delete p.numPlayersTradedAwayNormalized;

	// Handle stats if the season is in progress. Otherwise, not needed until next season.
	if (phase <= PHASE.PLAYOFFS) {
		setJerseyNumber(p, await genJerseyNumber(p));
	}

	let score = p.valueFuzz - 45;
	if (isSport("football")) {
		score -= 7;
	}
	score = Math.round(helpers.bound(score, 0, Infinity));

	setContract(p, contract, true);
	// Up to 18 he signs straight into the club's minors
	const signedTid = p.tid;
	placeYoungInMinors(p);
	const resigning =
		phase === PHASE.RESIGN_PLAYERS && p.draft.year !== g.get("season");
	const eventType = resigning ? "reSigned" : "freeAgent";
	const eid = await logEvent({
		type: eventType,
		showNotification: false,
		pids: [p.pid],
		tids: [signedTid],
		score,
		contract: p.contract,
	});

	const freeAgent = !resigning && !isRookie;
	if (freeAgent) {
		if (!p.transactions) {
			p.transactions = [];
		}
		p.transactions.push({
			season: g.get("season"),
			phase: g.get("phase"),
			tid: signedTid,
			type: "freeAgent",
			eid,
		});
	}

	return eid;
};

export default sign;
